import type { BlockContent, Content, PhrasingContent } from 'mdast';

// The gdoc importer keeps tables as raw single-line HTML in the markdown
// (GFM tables can't express colspan/rowspan or block content in cells).
// remark passes that through as an opaque `html` node, so nothing inside a
// table — footnote markers, links, emphasis — would ever reach the normal
// mdast rendering pipeline. This module parses that HTML into a structured
// `htmlTable` node whose cells hold ordinary mdast content, so tables render
// as first-class React with working footnotes instead of via
// dangerouslySetInnerHTML.
//
// The importer's HTML is machine-generated and uses a tiny vocabulary
// (table/tbody/tr/td, p, strong, br, occasionally a/em/ul/li, plus a few
// entities), so a small deterministic parser covers it fully. Anything
// outside that vocabulary returns null and the caller keeps the raw `html`
// node — rendering falls back to the legacy innerHTML path rather than
// dropping content.

export interface HtmlTableCell {
  header: boolean;
  colspan: number;
  rowspan: number;
  // From the cell's style attribute (gdoc exports use text-align for number
  // columns and background-color for heatmap shading). Only whitelisted
  // properties land here; a cell with any other property fails the whole
  // parse so the table falls back to raw-HTML rendering instead of
  // silently losing styling.
  style?: { textAlign?: 'left' | 'center' | 'right' | 'justify'; backgroundColor?: string };
  children: Content[];
}

export interface HtmlTableRow {
  cells: HtmlTableCell[];
}

export interface HtmlTableNode {
  type: 'htmlTable';
  rawHtml: string;
  rows: HtmlTableRow[];
}

// ─── HTML tokenizer / tree builder ──────────────────────────────────

interface HtmlElement {
  kind: 'element';
  tag: string;
  attrs: Record<string, string>;
  children: HtmlNode[];
}
interface HtmlText {
  kind: 'text';
  value: string;
}
type HtmlNode = HtmlElement | HtmlText;

const TAG_RE = /<(\/?)([a-zA-Z][a-zA-Z0-9]*)((?:\s+[a-zA-Z-]+(?:\s*=\s*"[^"]*")?)*)\s*(\/?)>/g;
const ATTR_RE = /([a-zA-Z-]+)(?:\s*=\s*"([^"]*)")?/g;
const VOID_TAGS = new Set(['br', 'hr', 'img']);

function decodeEntities(text: string): string {
  return text
    .replace(/&#(\d+);/g, (_m, code: string) => String.fromCodePoint(Number(code)))
    .replace(/&nbsp;/gi, ' ')
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .replace(/&quot;/gi, '"')
    .replace(/&apos;/gi, "'")
    .replace(/&amp;/gi, '&');
}

// Parses the fragment into a tree. Returns null on structural problems
// (mismatched close tags, unclosed elements) — never guesses.
function parseHtmlFragment(html: string): HtmlNode[] | null {
  const root: HtmlElement = { kind: 'element', tag: '#root', attrs: {}, children: [] };
  const stack: HtmlElement[] = [root];
  let lastIndex = 0;
  for (const match of html.matchAll(TAG_RE)) {
    const [full, closeSlash, rawTag, rawAttrs, selfSlash] = match;
    const text = html.slice(lastIndex, match.index);
    if (text) stack[stack.length - 1].children.push({ kind: 'text', value: decodeEntities(text) });
    lastIndex = match.index + full.length;
    const tag = rawTag.toLowerCase();
    if (closeSlash) {
      if (stack.length < 2 || stack[stack.length - 1].tag !== tag) return null;
      stack.pop();
      continue;
    }
    const attrs: Record<string, string> = {};
    for (const attr of rawAttrs.matchAll(ATTR_RE)) {
      attrs[attr[1].toLowerCase()] = attr[2] !== undefined ? decodeEntities(attr[2]) : '';
    }
    const element: HtmlElement = { kind: 'element', tag, attrs, children: [] };
    stack[stack.length - 1].children.push(element);
    if (!selfSlash && !VOID_TAGS.has(tag)) stack.push(element);
  }
  const trailing = html.slice(lastIndex);
  if (trailing.trim()) root.children.push({ kind: 'text', value: decodeEntities(trailing) });
  if (stack.length !== 1) return null;
  return root.children;
}

// ─── HTML tree → mdast content ──────────────────────────────────────

// Footnote markers survive inside raw HTML as literal "[^N]" text; split
// them out into real footnoteReference nodes so they render via FootnoteRef.
function textToPhrasing(value: string): PhrasingContent[] {
  const out: PhrasingContent[] = [];
  let last = 0;
  for (const match of value.matchAll(/\[\^(\d+)\]/g)) {
    if (match.index > last) out.push({ type: 'text', value: value.slice(last, match.index) });
    out.push({ type: 'footnoteReference', identifier: match[1], label: match[1] });
    last = match.index + match[0].length;
  }
  if (last < value.length) out.push({ type: 'text', value: value.slice(last) });
  return out;
}

// Thrown when cell content is outside the supported vocabulary in a way
// that can't be represented faithfully; parseGdocTableHtml catches it and
// returns null so the table falls back to raw-HTML rendering.
class UnsupportedHtmlError extends Error {}

const INLINE_WRAPPERS: Record<string, 'strong' | 'emphasis' | 'delete'> = {
  strong: 'strong',
  b: 'strong',
  em: 'emphasis',
  i: 'emphasis',
  s: 'delete',
  del: 'delete',
  strike: 'delete',
};

function toPhrasing(nodes: HtmlNode[]): PhrasingContent[] {
  const out: PhrasingContent[] = [];
  for (const node of nodes) {
    if (node.kind === 'text') {
      out.push(...textToPhrasing(node.value));
      continue;
    }
    const wrapper = INLINE_WRAPPERS[node.tag];
    if (wrapper) {
      // Empty wrappers (<strong></strong>) are kept: faithfulness over tidiness.
      out.push({ type: wrapper, children: toPhrasing(node.children) });
    } else if (node.tag === 'a') {
      // Bookmark anchors (<a id="kix…"> with no href) are link targets, not
      // links — mdast can't express them, so don't guess.
      if (node.attrs.href === undefined || node.attrs.id !== undefined) throw new UnsupportedHtmlError();
      out.push({ type: 'link', url: node.attrs.href, children: toPhrasing(node.children) });
    } else if (node.tag === 'br') {
      out.push({ type: 'break' });
    } else {
      // Unknown inline tag (e.g. span): keep its content, drop the tag.
      out.push(...toPhrasing(node.children));
    }
  }
  return out;
}

const isBlankText = (node: HtmlNode) => node.kind === 'text' && node.value.trim() === '';

function toBlockContent(nodes: HtmlNode[]): BlockContent[] {
  const out: BlockContent[] = [];
  // Bare inline content between block elements gathers into a paragraph.
  let inlineRun: HtmlNode[] = [];
  const flushInline = () => {
    const children = toPhrasing(inlineRun);
    inlineRun = [];
    if (children.length > 0) out.push({ type: 'paragraph', children });
  };
  for (const node of nodes) {
    if (node.kind === 'element' && node.tag === 'p') {
      flushInline();
      // Empty <p></p> is kept — it occupies vertical space when rendered,
      // so dropping it would change cell heights.
      out.push({ type: 'paragraph', children: toPhrasing(node.children) });
    } else if (node.kind === 'element' && (node.tag === 'ul' || node.tag === 'ol')) {
      flushInline();
      out.push({
        type: 'list',
        ordered: node.tag === 'ol',
        children: node.children
          .filter((child): child is HtmlElement => child.kind === 'element' && child.tag === 'li')
          .map((li) => ({ type: 'listItem', children: toBlockContent(li.children) })),
      });
    } else if (!isBlankText(node)) {
      inlineRun.push(node);
    }
  }
  flushInline();
  return out;
}

// ─── table extraction ───────────────────────────────────────────────

function findRows(element: HtmlElement): HtmlElement[] | null {
  const rows: HtmlElement[] = [];
  for (const child of element.children) {
    if (isBlankText(child)) continue;
    if (child.kind !== 'element') return null;
    if (child.tag === 'thead' || child.tag === 'tbody' || child.tag === 'tfoot') {
      const nested = findRows(child);
      if (nested === null) return null;
      rows.push(...nested);
    } else if (child.tag === 'tr') {
      rows.push(child);
    } else {
      return null;
    }
  }
  return rows;
}

function parseSpan(value: string | undefined): number {
  const span = Number(value ?? 1);
  return Number.isInteger(span) && span >= 1 ? span : 1;
}

const TEXT_ALIGN_VALUES = ['left', 'center', 'right', 'justify'] as const;

// Returns undefined for no/empty style, the parsed object for whitelisted
// declarations, and null for anything else (→ whole-table fallback).
function parseCellStyle(value: string | undefined): HtmlTableCell['style'] | null | undefined {
  if (!value || !value.trim()) return undefined;
  const style: NonNullable<HtmlTableCell['style']> = {};
  for (const declaration of value.split(';')) {
    if (!declaration.trim()) continue;
    const [prop, ...rest] = declaration.split(':');
    const propValue = rest.join(':').trim();
    const propName = prop.trim().toLowerCase();
    if (propName === 'text-align' && (TEXT_ALIGN_VALUES as readonly string[]).includes(propValue)) {
      style.textAlign = propValue as (typeof TEXT_ALIGN_VALUES)[number];
    } else if (propName === 'background-color' && propValue) {
      style.backgroundColor = propValue;
    } else {
      return null;
    }
  }
  return style;
}

/**
 * Parse a raw-HTML table (as produced by the gdoc importer) into a
 * structured `htmlTable` node. Returns null if the HTML is anything other
 * than a single well-formed table — callers keep the raw `html` node then.
 */
export function parseGdocTableHtml(html: string): HtmlTableNode | null {
  const fragment = parseHtmlFragment(html);
  if (!fragment) return null;
  const significant = fragment.filter((node) => !isBlankText(node));
  if (significant.length !== 1) return null;
  const [table] = significant;
  if (table.kind !== 'element' || table.tag !== 'table') return null;
  // Nested tables are outside the importer's vocabulary; don't guess.
  const hasNestedTable = (nodes: HtmlNode[]): boolean =>
    nodes.some((n) => n.kind === 'element' && (n.tag === 'table' || hasNestedTable(n.children)));
  if (hasNestedTable(table.children)) return null;

  const rowElements = findRows(table);
  if (rowElements === null || rowElements.length === 0) return null;

  const rows: HtmlTableRow[] = [];
  for (const rowElement of rowElements) {
    const cells: HtmlTableCell[] = [];
    for (const child of rowElement.children) {
      if (isBlankText(child)) continue;
      if (child.kind !== 'element' || (child.tag !== 'td' && child.tag !== 'th')) return null;
      const style = parseCellStyle(child.attrs.style);
      if (style === null) return null;
      let children: BlockContent[];
      try {
        children = toBlockContent(child.children);
      } catch (e) {
        if (e instanceof UnsupportedHtmlError) return null;
        throw e;
      }
      cells.push({
        header: child.tag === 'th',
        colspan: parseSpan(child.attrs.colspan),
        rowspan: parseSpan(child.attrs.rowspan),
        ...(style ? { style } : {}),
        children,
      });
    }
    rows.push({ cells });
  }
  return { type: 'htmlTable', rawHtml: html, rows };
}
